import { parse as parseYaml } from 'yaml';

export interface BlogPostFrontmatter {
  title: string;
  slug: string;
  description: string;
  author: string;
  published: string;
  updated: string;
  pillar: string;
  tags?: string[];
  schema?: string;
  canonical?: string;
  targetKeywords?: string[];
  relatedLandingPages?: string[];
  relatedPosts?: string[];
  ctaNiche?: string;
  jsonLd?: string[];
}

export interface BlogPost {
  meta: BlogPostFrontmatter;
  content: string;
}

/** Browser-safe frontmatter split — gray-matter uses Node `Buffer` and breaks in Vite client bundles. */
function parseMarkdownFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  const text = raw.startsWith('\uFEFF') ? raw.slice(1) : raw;
  if (!text.startsWith('---')) {
    return { data: {}, content: text.trim() };
  }

  const end = text.indexOf('\n---', 3);
  if (end === -1) {
    return { data: {}, content: text.trim() };
  }

  const yamlBlock = text.slice(4, end);
  const content = text.slice(end + 4).replace(/^\r?\n/, '').trim();

  try {
    const data = parseYaml(yamlBlock);
    return { data: (data as Record<string, unknown>) ?? {}, content };
  } catch {
    return { data: {}, content: text.trim() };
  }
}

const rawModules = import.meta.glob<string>('../content/blog/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

function parsePosts(): BlogPost[] {
  return Object.entries(rawModules)
    .map(([, raw]) => {
      const { data, content } = parseMarkdownFrontmatter(raw);
      return {
        meta: data as unknown as BlogPostFrontmatter,
        content,
      };
    })
    .filter((p) => p.meta.slug)
    .sort((a, b) => b.meta.published.localeCompare(a.meta.published));
}

let cache: BlogPost[] | null = null;

export function getAllBlogPosts(): BlogPost[] {
  if (!cache) cache = parsePosts();
  return cache;
}

export function getBlogPost(slug: string): BlogPost | undefined {
  return getAllBlogPosts().find((p) => p.meta.slug === slug);
}

export function getPostsByPillar(pillar: string): BlogPost[] {
  return getAllBlogPosts().filter((p) => p.meta.pillar === pillar);
}

export const BLOG_PILLARS: { id: string; label: string }[] = [
  { id: 'retention', label: 'Customer retention' },
  { id: 'agreements', label: 'Maintenance agreements' },
  { id: 'winback', label: 'Win-back & reactivation' },
  { id: 'pricing', label: 'Pricing & memberships' },
  { id: 'tools', label: 'Tools & comparisons' },
];

/** Strip leading # title — we render title in the page header for SEO. */
export function stripLeadingH1(content: string): string {
  return content.replace(/^#\s+.+\n+/, '');
}

export function formatBlogDate(iso: string): string {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
